import { applyTextEdit } from "../model/document"
import { getTextEditError, removeAnnotation } from "../model/entityRanges"
import type {
  CodecResult,
  FormattingTarget,
  MessageContent,
  NativeMessageEntity,
  ParsedMessage,
  VisualEdit,
} from "../types"
import {
  entityDiagnostics,
  failure,
  hasValidUnicode,
  hasUnknownEntityMetadata,
  hasUnknownEntityType,
  isUtf16Boundary,
  sameSemantics,
  sourceDiagnostics,
} from "./shared"
import { parseTelegramHtml, serializeTelegramHtml } from "./telegramHtml"
import {
  parseTelegramMarkdown,
  serializeTelegramMarkdown,
} from "./telegramMarkdown"

function sameFormatting(a: FormattingTarget, b: FormattingTarget): boolean {
  return (
    a.kind === b.kind &&
    (a.kind !== "markup" ||
      (b.kind === "markup" && a.parseMode === b.parseMode))
  )
}

/** Parse a derived semantic baseline for the visual adapter, without mutating
 * source or resolving markers. Unknown native metadata is preserved verbatim. */
export function parseMessageContent(
  content: MessageContent,
): CodecResult<ParsedMessage> {
  if (typeof content.text !== "string")
    return failure("INVALID_TEXT", "Text must be a string")
  const formatting = content.formatting
  if (!formatting || !["plain", "markup", "entities"].includes(formatting.kind))
    return failure(
      "INVALID_FORMATTING",
      "Unknown formatting discriminator",
      "/formatting",
    )
  const keys =
    formatting.kind === "markup"
      ? ["kind", "parseMode"]
      : formatting.kind === "entities"
        ? ["kind", "entities"]
        : ["kind"]
  if (Object.keys(formatting).some((key) => !keys.includes(key)))
    return failure(
      "INVALID_FORMATTING",
      "Formatting fields must match the discriminator",
      "/formatting",
    )
  if (formatting.kind === "markup") {
    switch (formatting.parseMode) {
      case "HTML":
        return parseTelegramHtml(content.text)
      case "Markdown":
      case "MarkdownV2":
        return parseTelegramMarkdown(content.text, formatting.parseMode)
      default:
        return failure(
          "INVALID_PARSE_MODE",
          "Unsupported parse mode",
          "/formatting/parseMode",
        )
    }
  }
  const entities = formatting.kind === "entities" ? formatting.entities : []
  if (
    !Array.isArray(entities) ||
    entities.some(
      (entity) =>
        !entity ||
        typeof entity !== "object" ||
        typeof entity.type !== "string",
    )
  )
    return failure(
      "INVALID_ENTITIES",
      "Entities must be an array of entity objects",
      "/formatting/entities",
    )
  const diagnostics = entityDiagnostics(content.text, entities).filter(
    (item) => item.severity === "error",
  )
  if (diagnostics.length) return { ok: false, diagnostics }
  return {
    ok: true,
    value: {
      source: content.text,
      formatting: { kind: formatting.kind },
      text: content.text,
      entities: [...entities],
      mapping: [
        {
          kind: "text",
          sourceRange: { offset: 0, length: content.text.length },
          textRange: { offset: 0, length: content.text.length },
        },
      ],
    },
  }
}

function writeContent(
  content: MessageContent,
  parsed: ParsedMessage,
  target: FormattingTarget,
): CodecResult<MessageContent> {
  if (target.kind === "plain") {
    if (parsed.entities.length)
      return failure(
        "LOSSY_CONVERSION",
        "Plain text cannot preserve annotations; remove them explicitly before converting",
        "/formatting",
      )
    return {
      ok: true,
      value: { ...content, text: parsed.text, formatting: { kind: "plain" } },
    }
  }
  if (target.kind === "entities") {
    const entities = parsed.entities
    if (
      !entities.every(
        (entity): entity is NativeMessageEntity =>
          entity.type !== "mention_by_id",
      )
    )
      return failure(
        "MISSING_USER_METADATA",
        "An ID-only mention cannot create a native User object",
        "/formatting",
      )
    return {
      ok: true,
      value: {
        ...content,
        text: parsed.text,
        formatting: {
          kind: "entities",
          entities,
        },
      },
    }
  }
  const serialized =
    target.parseMode === "HTML"
      ? serializeTelegramHtml(parsed)
      : serializeTelegramMarkdown(parsed, target.parseMode)
  if (!serialized.ok) return serialized
  return {
    ok: true,
    value: { ...content, text: serialized.value, formatting: { ...target } },
  }
}

/** Only explicit format changes rewrite source. Same-target selection (including
 * on an invalid raw draft) is a true identity operation, not normalization. */
export function convertFormatting(
  content: MessageContent,
  target: FormattingTarget,
): CodecResult<MessageContent> {
  if (sameFormatting(content.formatting, target))
    return { ok: true, value: content }
  const parsed = parseMessageContent(content)
  return parsed.ok ? writeContent(content, parsed.value, target) : parsed
}

/** Atomically apply semantic operations, then write through the ORIGINAL codec.
 * No-op/view toggles return the original object. On failure callers still own
 * the unchanged source. The baseline must match both source and semantics. */
export function applyVisualEdit(
  content: MessageContent,
  edit: VisualEdit,
  parsedBaseline: ParsedMessage,
): CodecResult<MessageContent> {
  const current = parseMessageContent(content)
  if (!current.ok) return current
  if (
    parsedBaseline.source !== content.text ||
    !sameFormatting(parsedBaseline.formatting, content.formatting) ||
    !sameSemantics(parsedBaseline, current.value)
  )
    return failure(
      "STALE_BASELINE",
      "Visual edit baseline no longer matches the source",
    )
  let text = current.value.text
  let entities = [...current.value.entities]
  for (const operation of edit.operations) {
    if (operation.kind === "text") {
      const error = getTextEditError(operation, text.length)
      if (error) return failure("INVALID_EDIT_RANGE", error)
      if (
        !isUtf16Boundary(text, operation.from) ||
        !isUtf16Boundary(text, operation.to) ||
        !hasValidUnicode(operation.text)
      )
        return failure(
          "SPLIT_SURROGATE",
          "An edit cannot split a UTF-16 surrogate pair",
        )
      if (text.slice(operation.from, operation.to) === operation.text) continue
      const opaqueIndex = entities.findIndex(
        (entity) =>
          hasUnknownEntityMetadata(entity) &&
          (operation.from === operation.to
            ? operation.from > entity.offset &&
              operation.from < entity.offset + entity.length
            : operation.from < entity.offset + entity.length &&
              operation.to > entity.offset),
      )
      if (opaqueIndex !== -1)
        return failure(
          "UNSAFE_VISUAL_EDIT",
          "Edit intersects unrecognized annotation metadata; remove the annotation explicitly first",
          `/formatting/entities/${opaqueIndex}`,
        )
      // applyTextEdit owns slot atomicity, opaque intersections, range affinity
      // and collision prevention for links. The native signature excludes
      // ID-only markup mentions; the mapper preserves them as opaque data.
      const mapped = applyTextEdit(
        {
          text,
          formatting: {
            kind: "entities",
            entities: entities as NativeMessageEntity[],
          },
          mediaIds: [],
          buttons: [],
        },
        operation,
      )
      if (!mapped.ok)
        return failure(
          "UNSAFE_VISUAL_EDIT",
          mapped.reason,
          mapped.entityIndex === undefined ||
            content.formatting.kind === "markup"
            ? "/text"
            : `/formatting/entities/${mapped.entityIndex}`,
        )
      text = mapped.content.text
      if (mapped.content.formatting.kind === "entities")
        entities = mapped.content.formatting.entities
    } else if (operation.kind === "removeEntity") {
      const removed = removeAnnotation(
        entities as NativeMessageEntity[],
        operation.index,
      )
      if (!removed.ok)
        return failure(
          "INVALID_ANNOTATION_INDEX",
          removed.reason,
          "/formatting/entities",
        )
      entities = removed.entities
    } else if (operation.kind === "addEntity") {
      // Adding a style/link is still a visual edit. Retaining an opaque sidecar
      // must not imply that its intersection semantics are understood.
      const opaqueIndex = entities.findIndex(
        (entity) =>
          (hasUnknownEntityMetadata(entity) || hasUnknownEntityType(entity)) &&
          operation.entity.offset < entity.offset + entity.length &&
          operation.entity.offset + operation.entity.length > entity.offset,
      )
      if (opaqueIndex !== -1)
        return failure(
          "UNSAFE_VISUAL_EDIT",
          "Formatting intersects an unrecognized annotation",
          `/formatting/entities/${opaqueIndex}`,
        )
      entities.push({ ...operation.entity })
    }
  }
  if (sameSemantics(current.value, { text, entities }))
    return { ok: true, value: content }
  const diagnostics = entityDiagnostics(text, entities).filter(
    (item) => item.severity === "error",
  )
  const edited = { ...current.value, text, entities, mapping: [] }
  if (diagnostics.length)
    return { ok: false, diagnostics: sourceDiagnostics(edited, diagnostics) }
  return writeContent(content, edited, current.value.formatting)
}
